import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Amplify } from "aws-amplify";
import { AuthUser, fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { BehaviorSubject, from, Observable, of, Subject, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BaseResponseDetail } from '../../../shared/interfaces/base-response/base-response.interface';
import { IToken } from '../models/auth.model';
import { UserType } from './auth.service';
import { ThemeConfig } from 'src/app/shared/services/theme/theme.service';
import { SignalRService } from 'src/app/shared/services/signalr/signal-r.service';

export interface IUser {
  email: string;
  password: string;
  showPassword?: boolean;
  code?: string;
  name?: string;
}
@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private apiUrl = environment.apiUrl;

  private authenticationSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentUserSubject: BehaviorSubject<UserType> = new BehaviorSubject<UserType>(undefined);

  private logoutSubject = new Subject<void>();

  logout$ = this.logoutSubject.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
    private _signalRService : SignalRService
  ) {
    Amplify.configure({
      Auth: {
        Cognito: {
          ...environment.cognito,
        },
      },
    })
  }

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  public signIn(user: IUser): Promise<any> {
    return signIn({
      username: user.email,
      password: user.password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH'
      }
    })
      .then(({ nextStep }) => {
        if (nextStep.signInStep == 'DONE') { }
      })
      .catch((error: any) => {
        console.error("Sign In Error:", error);
        throw error;
      });
  }

  public signOut(): Promise<any> {
    this.closeAllSessions().subscribe();

    return signOut({ global: true })
      .then(() => {
        this.logoutSubject.next();

        this.redirectToLogoutUrl();
        this.clearLocalStorage();
        this._signalRService.stopConnection('notifications')
      })
      .catch((error: any) => {
        console.error("Sign Out Error:", error);
        throw error;
      });
  }

  public isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return Promise.resolve(true);
    }
    return this.getUser()
      .then(user => !!user)
      .catch(() => false);
  }

  public getUser(): Promise<AuthUser | null> {
    return getCurrentUser()
      .catch((error: any) => {
        return null; // Return null on error
      });
  }

  public getUserSession(): Promise<any> {
    return fetchAuthSession()
      .catch((error: any) => {
        console.error("Get User Session Error:", error);
      }
      )
  }

  public getAccessToken(): string | null {
    return localStorage.getItem('rbac-token');
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  public saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('rbac-token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  }

  public clearTokens(): void {
    localStorage.removeItem('rbac-token');
    localStorage.removeItem('refresh_token');
  }

  public generateToken(): Observable<any> {
    return this.http.get(`${this.apiUrl}/rbac/tokens/generates`);
  }

  public refreshToken(rbacToken: string): Observable<BaseResponseDetail<IToken>> {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getAccessToken();

    if (!refreshToken) throw new Error('No refresh token found');

    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${rbacToken}`)
      .set('Application-Name', 'Solvex-RBAC');

    return this.http.post<BaseResponseDetail<IToken>>(`${this.apiUrl}/rbac/tokens/refresh`, { refreshToken, accessToken }, { headers })
      .pipe(
        tap(() => {
          localStorage.setItem('last_refreshed_at', Date.now().toString())
        })
      );
  }

  public closeAllSessions(): Observable<any> {
    const rbacToken = this.getAccessToken();

    if (!rbacToken) {
      return of(false)
    }

    const fetchPromise = fetch(`${this.apiUrl}/rbac/users/close-log-sessions`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${rbacToken}`,
        'Application-Name': 'Solvex-RBAC'
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        console.error('Error closing all sessions:', error);
        throw error;
      });

    return from(fetchPromise);
  }

  public logout(): void {
    this.clearTokens();
    this.redirectToLogoutUrl();
  }

  public redirectToLogoutUrl(): void {
    const localData = localStorage.getItem('themeConfig');
    let routeSegment = '';

    if (localData) {
      try {
        const parsed: ThemeConfig = JSON.parse(localData);
        routeSegment = parsed.nameByRoute || '';
      } catch (e) {}
    }

    this.router.navigate(['/login', routeSegment].filter(Boolean));
  }

  public clearLocalStorage(): void {
    const keyToKeep = 'themeConfig';

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key: string | null = localStorage.key(i);
      if (key !== keyToKeep) {
        localStorage.removeItem(key!);
      }
    }
  }
}
